import React, { useState } from "react";
import { Modal, Input, Button } from "antd";
import { useSnackbar } from "notistack";
import "./SupportReviewForm.css";

const MAX_LENGTH = 1000;

const COPY = {
  feedback: {
    title: "Send feedback",
    intro: "Tell us what is working, or what is getting in your way.",
    label: "Your feedback",
    placeholder: "What would you change about Tooth Finder?",
    submit: "Send feedback",
    success: "Thanks, your feedback was sent",
  },
  review: {
    title: "Write a review",
    intro: "Share how Tooth Finder is working out for your practice.",
    label: "Your review",
    placeholder: "What has your experience been like so far?",
    submit: "Submit review",
    success: "Thanks, your review was submitted",
  },
};

const SupportReviewForm = ({ open, onClose, type, userDetails }) => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const copy = COPY[type === "feedback" ? "feedback" : "review"];
  const trimmed = message.trim();
  const canSubmit = trimmed.length > 0 && !loading;

  const handleSubmit = async () => {
    if (!trimmed) {
      enqueueSnackbar("Please enter a message", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/open/v1/support`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: userDetails?.fullName || "Unknown User",
            email: userDetails?.email || "",
            userId: userDetails?.id || userDetails?._id || "",
            message: trimmed,
            type: type,
          }),
        }
      );

      if (response.ok) {
        enqueueSnackbar(copy.success, { variant: "success" });
        setMessage("");
        onClose();
      } else {
        const errorData = await response.json();
        enqueueSnackbar(
          errorData.message || "Failed to submit. Please try again.",
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      enqueueSnackbar("Network error. Please check your connection.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={copy.title}
      centered
      width={520}
      className="feedback-modal"
      maskClosable={!loading}
      destroyOnClose
      footer={
        <div className="feedback-actions">
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            className="feedback-submit"
            loading={loading}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {copy.submit}
          </Button>
        </div>
      }
    >
      <p className="feedback-intro">{copy.intro}</p>

      {/* Name and email were previously rendered as two read-only text
          inputs. They are not editable, so showing them as form fields
          implied otherwise and cost about 140px of height. They are still
          sent with the request, just shown as attribution. */}
      <p className="feedback-identity">
        Sending as <strong>{userDetails?.fullName || "Unknown User"}</strong>
        {userDetails?.email ? (
          <span className="feedback-identity-email">{userDetails.email}</span>
        ) : null}
      </p>

      <label className="feedback-label" htmlFor="feedback-message">
        {copy.label}
      </label>
      <Input.TextArea
        id="feedback-message"
        rows={5}
        maxLength={MAX_LENGTH}
        placeholder={copy.placeholder}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={loading}
        autoFocus
      />
      <p className="feedback-count">
        {message.length}/{MAX_LENGTH}
      </p>
    </Modal>
  );
};

export default SupportReviewForm;
